import React, {useState} from 'react'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {
  BreakdownType,
  Price as BookingPrice
} from '@vio/core/src/api-types/bovio/response/booking'
import {TaxesType} from '@vio/core/src/api-types/bovio/response/offer_check'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {CurrencyDisplayVariant} from '@vio/core/src/price/business/formatPrice'
import {
  getDisplayPrice,
  getPriceDetails
} from '@vio/core/src/price/business/price'

import {TaxesAndFees} from '../TaxesAndFees'
import {FeeTypes} from '.'
import {PriceLine} from './PriceLine'
import {
  getTaxesAndFeesPopupBodyText,
  getTaxesAndFeesPopupHeaderText
} from './TaxesAndFeesMessages'
import {toPayAtPropertyFeeLines} from './toPayAtPropertyFeeLines'
import {TranslatedOtherFees} from './TranslatedOtherFees'
interface PriceBreakdownProps {
  displayTransparentFees: boolean
  showFeesDescriptions: boolean
  onFeesDetailsClick?: () => void
  isPriceLoading?: boolean
  currencyDisplayVariant?: CurrencyDisplayVariant
  /** Prices */
  prices: OfferPrice[] | BookingPrice[]
  /** Whether the price is pay at property */
  isPayAtProperty: boolean
  showTaxes?: boolean
  numberOfRooms: number
  numberOfNights: number
  displayNumberOfRooms?: boolean
  showTaxesAndFeesCombined?: boolean
}

export const PriceBreakdown = ({
  displayTransparentFees = false,
  showFeesDescriptions,
  isPriceLoading = false,
  onFeesDetailsClick,
  currencyDisplayVariant,
  prices,
  isPayAtProperty,
  numberOfRooms,
  numberOfNights,
  displayNumberOfRooms = true,
  showTaxesAndFeesCombined = false
}: PriceBreakdownProps) => {
  const [taxesAndFeesModalOpen, setTaxesAndFeesModalOpen] = useState(false)
  const {brandName} = useBrand()

  // Using this value only for offer-check and booking-retrieve as it has the detailed hotel fees breakdown
  const displayPrice = getDisplayPrice<OfferPrice | BookingPrice>(prices)

  const {
    displayCurrencyCode,
    taxesDisplayPrice,
    hotelFeesTotal,
    displayPriceBase
  } = getPriceDetails(prices, isPayAtProperty)

  const taxes = Number(taxesDisplayPrice)
  const currencyCode = String(displayCurrencyCode)
  const totalTaxes = taxes + hotelFeesTotal
  const isVat =
    displayPrice &&
    'taxes' in displayPrice &&
    displayPrice.taxes &&
    displayPrice.taxes.breakdown.length === 1 &&
    displayPrice.taxes.breakdown[0].type === TaxesType.Vat
  const payAtPropertyFees = toPayAtPropertyFeeLines(
    displayPrice?.hotelFees?.breakdown
  )
  // Pay-at-property offers already carry the label in the offer perks, so
  // repeating it on every fee line is redundant. Only MoR offers need it here.
  const showPayAtPropertyLabel = !isPayAtProperty

  const handleTaxesAndFeesClose = () => {
    setTaxesAndFeesModalOpen(false)
  }

  const onInfoPopupOpen = () => {
    onFeesDetailsClick?.()
    setTaxesAndFeesModalOpen(isOpen => !isOpen)
  }
  return (
    <>
      {displayTransparentFees && (
        <Box paddingBottom="s400">
          <Text variant="titleM">
            <FormattedMessageWrapper
              id="priceDetails"
              defaultMessage="Price details"
            />
          </Text>
        </Box>
      )}
      <Box
        gap="s250"
        paddingBottom="s400"
        display="flex"
        flexDirection="column"
      >
        {/* Following 2 rows, Base rate and tax rows are taken from chargeable prices */}
        {displayTransparentFees && (
          <PriceLine
            label={
              displayNumberOfRooms ? (
                <FormattedMessageWrapper
                  id="priceDetails.roomsAndNights"
                  defaultMessage="{rooms, plural, =0 {} one {1 room x } other {# rooms x }}{nights, plural, one {1 night} other {# nights}}"
                  values={{
                    rooms: numberOfRooms,
                    nights: numberOfNights
                  }}
                />
              ) : (
                <FormattedMessageWrapper
                  id="priceDetails.numberOfNights"
                  defaultMessage="{nights, plural, one {1 night} other {# nights}}"
                  values={{
                    nights: numberOfNights
                  }}
                />
              )
            }
            price={displayPriceBase}
            currencyCode={currencyCode}
          />
        )}
        {totalTaxes > 0 ? (
          !displayTransparentFees || showTaxesAndFeesCombined ? (
            <Box paddingBottom="s200">
              <PriceLine
                label={getTaxesAndFeesPopupHeaderText(FeeTypes.TaxesFees)}
                isPriceLoading={isPriceLoading}
                showFeesDescriptions={showFeesDescriptions}
                price={totalTaxes}
                currencyCode={currencyCode}
                onInfoClick={onInfoPopupOpen}
                currencyDisplayVariant={currencyDisplayVariant}
              />
              <TaxesAndFees
                currencyCode={currencyCode}
                hotelFeesTotal={hotelFeesTotal}
                taxes={taxes}
                isOpen={taxesAndFeesModalOpen}
                onClose={handleTaxesAndFeesClose}
              />
            </Box>
          ) : taxes > 0 ? (
            <PriceLine
              label={getTaxesAndFeesPopupHeaderText(
                isVat ? TaxesType.Vat : TaxesType.Tax
              )}
              tooltipBody={
                !isVat
                  ? getTaxesAndFeesPopupBodyText(TaxesType.Tax, brandName)
                  : null
              }
              isPriceLoading={isPriceLoading}
              showFeesDescriptions={!isVat}
              price={taxes}
              currencyCode={currencyCode}
              currencyDisplayVariant={currencyDisplayVariant}
            />
          ) : null
        ) : null}

        {/* Following rows are taken from hotelFees and are pay at property */}
        {displayTransparentFees &&
          !showTaxesAndFeesCombined &&
          payAtPropertyFees.map(fee => (
            <PriceLine
              key={fee.type}
              isPayAtProperty={showPayAtPropertyLabel}
              label={getTaxesAndFeesPopupHeaderText(fee.type)}
              price={fee.total}
              currencyCode={currencyCode}
              currencyDisplayVariant={currencyDisplayVariant}
              {...(fee.type === BreakdownType.Other &&
                fee.elements?.length && {
                  tooltipBody: (
                    <TranslatedOtherFees
                      elements={fee.elements as BreakdownType[]}
                    />
                  ),
                  showFeesDescriptions: true
                })}
            />
          ))}
      </Box>
    </>
  )
}
